import React, { useEffect } from 'react'
import { PropTypes } from 'prop-types'
import SanityImage from 'gatsby-plugin-sanity-image'
import Portal from '../portal'

import {
	CapHeader,
	SectionTitle,
	VSMModalBackdrop,
	Title,
	VSMModalWrapper,
	VSMModalScroll,
	Superpower,
	DaNV,
	VendorIcons,
	VendorGridItem,
	UsesAndImprovements,
	Points,
	Example,
	VSMCTA,
	RecommendedReading,
	TopBar,
	ModalTab,
	CloseModalButton,
	Newspaper,
	ThreeQuarter,
	SuperpowerIcons,
	VSMRRGridContent,
	VSMRRGridLink,
	VSMRRGridMedia,
	VSMRRGridColumn,
	VSMFourColumnRow,
	VSMModalOverlay,
} from './VSMModal.styles'
import PortableText from '../portableText'
import CTALink from '../CTALink'

const ModalCopy = ({ portableTextBlock }) => <PortableText blocks={portableTextBlock} />

const Modal = ({ item, forgetModal }) => {
	const {
		columnStart,
		title,
		superPower,
		superheroIcon,
		secondaryIcon,
		vendors,
		purpose,
		example,
		recommendedReading,
		recommendedReadingCopy,
		vsmHelp,
		vsmUses,
		vsmCallToAction,
	} = item

	useEffect(() => {
		const escape = (e) => {
			if (e.key === 'Escape') forgetModal()
		}

		window.addEventListener('keydown', escape)
		return () => window.removeEventListener('keydown', escape)
	}, [])

	let capIt = ''
	let tabTitle = ''

	if (columnStart === 'ideate') {
		capIt = `${columnStart}`.toUpperCase()
		tabTitle = `1. ${columnStart}`
	}
	if (columnStart === 'create') {
		capIt = `${columnStart}`.toUpperCase()
		tabTitle = `2. ${columnStart}`
	}
	if (columnStart === 'release') {
		capIt = `${columnStart}`.toUpperCase()
		tabTitle = `3. ${columnStart}`
	}
	if (columnStart === 'operate') {
		capIt = `${columnStart}`.toUpperCase()
		tabTitle = `4. ${columnStart}`
	}

	return (
		<Portal>
			<VSMModalBackdrop onClick={forgetModal} />
			<VSMModalWrapper className={`${columnStart}`}>
				<TopBar>
					<ModalTab>{tabTitle}</ModalTab>
				</TopBar>
				<CloseModalButton type="button" onClick={forgetModal}>
					<div />
					<div />
				</CloseModalButton>
				<VSMModalScroll>
					<Title>
						<SectionTitle>{title}</SectionTitle>
					</Title>
					<VSMModalOverlay>
						{superPower ? (
							<Superpower>
								<SuperpowerIcons>
									<SanityImage
										{...superheroIcon}
										asset={superheroIcon.asset}
										width={96}
										alt={superheroIcon.alt}
										className="superheroIcon"
									/>
									{secondaryIcon && (
										<SanityImage
											{...secondaryIcon}
											asset={secondaryIcon.asset}
											width={60}
											alt={secondaryIcon.alt}
											className="secondaryIcon"
										/>
									)}
								</SuperpowerIcons>
								<div className="copy">
									<CapHeader>Superpower:</CapHeader>
									<SectionTitle>{superPower}</SectionTitle>
								</div>
							</Superpower>
						) : null}

						{vendors ? (
							<DaNV>
								<CapHeader>Discipline and Notable Vendors:</CapHeader>
								<VendorIcons>
									{vendors.map((v) => (
										<VendorGridItem key={v._key}>
											{v._type === 'vsmVendorReference' && (
												<SanityImage
													asset={v.vendor.illustration.image.asset}
													width={96}
													alt={v.vendor.illustration.image.alt}
													{...v.vendor.illustration}
												/>
											)}
											{v._type === 'toolReference' && (
												<SanityImage
													asset={v.tool.logo.image.asset}
													width={96}
													alt={v.tool.logo.image.alt}
													{...v.tool.logo}
												/>
											)}
											{v._type === 'card' && (
												<SanityImage
													asset={v.illustration.image.asset}
													width={96}
													alt={v.illustration.image.alt}
													{...v.illustration}
												/>
											)}
											{/* {v._type === 'vsmVendorReference' ? <p>{v.vendor.title}</p> : <p>{v.tool.title}</p>} */}
										</VendorGridItem>
									))}
									<VendorGridItem className="submit-a-vendor">
										<a
											href={`mailto:info@tasktop.com?subject=Submit%20a%20Vendor%20for%20${capIt}`}
										>
											Submit a vendor
										</a>
									</VendorGridItem>
								</VendorIcons>
							</DaNV>
						) : null}
						<UsesAndImprovements>
							{purpose ? (
								<Points>
									<CapHeader>What do you use it for?</CapHeader>
									<ModalCopy {...purpose} />
								</Points>
							) : null}
							{vsmHelp ? (
								<Points>
									<CapHeader>How does VSM help the discipline improve?</CapHeader>
									<ModalCopy {...vsmHelp} />
								</Points>
							) : null}
						</UsesAndImprovements>

						{example ? (
							<Example>
								<SectionTitle>Example</SectionTitle>
								<ThreeQuarter>
									<ModalCopy {...example} />
								</ThreeQuarter>

								{vsmUses ? (
									<>
										<CapHeader>Here's how they'd use their VSM Tool:</CapHeader>
										<Newspaper>
											<ModalCopy {...vsmUses} />
										</Newspaper>
									</>
								) : null}
							</Example>
						) : null}

						{vsmCallToAction ? (
							<VSMCTA>
								<SectionTitle>{vsmCallToAction.title}</SectionTitle>
								<Points>
									<ModalCopy {...vsmCallToAction.vsmCTACopy} />
								</Points>
								<CTALink {...vsmCallToAction.cta} />
							</VSMCTA>
						) : null}

						{recommendedReading ? (
							<RecommendedReading>
								<SectionTitle>Recommended Reading</SectionTitle>
								{recommendedReadingCopy ? (
									<Points>
										<ModalCopy {...recommendedReadingCopy} />
									</Points>
								) : null}

								<VSMFourColumnRow>
									{recommendedReading.map((read) => (
										<VSMRRGridColumn key={read._key}>
											{read.illustration ? (
												<>
													{read.illustration.image.asset ? (
														<VSMRRGridMedia>
															<SanityImage
																asset={read.illustration.image.asset}
																alt={read.illustration.image.alt}
																{...read.illustration}
															/>
														</VSMRRGridMedia>
													) : null}
												</>
											) : null}
											{read.resource ? (
												<>
													{read.resource.image.asset ? (
														<VSMRRGridMedia>
															<SanityImage
																asset={read.resource.image.asset}
																alt={read.resource.image.alt}
																{...read.resource.image}
															/>
														</VSMRRGridMedia>
													) : null}
												</>
											) : null}
											<VSMRRGridContent>
												{read.resource ? (
													<>
														<p>{read.resource.title}</p>
														<VSMRRGridLink>
															<CTALink {...read.resource.cta} />
														</VSMRRGridLink>
													</>
												) : (
													<>
														<p>{read.title}</p>
														<VSMRRGridLink>
															<CTALink {...read.cta} />
														</VSMRRGridLink>
													</>
												)}
											</VSMRRGridContent>
										</VSMRRGridColumn>
									))}
								</VSMFourColumnRow>
							</RecommendedReading>
						) : null}
					</VSMModalOverlay>
				</VSMModalScroll>
			</VSMModalWrapper>
		</Portal>
	)
}

export default Modal

Modal.propTypes = {
	item: PropTypes.object,
	forgetModal: PropTypes.func,
}

ModalCopy.propTypes = {
	portableTextBlock: PropTypes.array,
}
